import React from "react";
import { Link } from "react-router-dom";
import { Store, Shield, Sparkles, ArrowRight, Apple, Smartphone } from "lucide-react";
import Footer from "../components/Footer";

// MarketplaceAppOnlyGate
// ---------------------------------------------------------------------
// Web visitors to `/marketplace` see this landing instead of the real
// marketplace UI. Native (iOS / Android) app users go straight to the
// real listing experience.
//
// Strategic rationale: the marketplace for second-hand sub-channels is
// the highest-friction, lowest-volume feature on the site at launch.
// Reserving it as a native-app-exclusive does two things at once:
//   (1) gives Apple a clear "native value" story for App Store review,
//       defending against guideline 4.2.3 thin-wrapper rejections;
//   (2) converts the curious-but-not-yet-installed crowd into installs.
//
// Copy intentionally elicits curiosity without describing pricing or
// listings — visitors should download to find out. The "Why?" line is
// the only place we explain the gate, framed as a security/trust pitch
// rather than a paywall.
const MarketplaceAppOnlyGate = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-950 to-black text-gray-200 flex flex-col" data-testid="marketplace-app-gate">
      <main className="flex-1 flex flex-col items-center justify-center px-4 py-12">
        <div className="max-w-2xl w-full text-center">

          {/* Sparkly badge */}
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-500/10 border border-amber-500/30 text-amber-300 text-xs uppercase tracking-wider mb-6" data-testid="marketplace-gate-badge">
            <Sparkles className="h-3.5 w-3.5" />
            App Exclusive
          </div>

          <Store className="h-12 w-12 text-emerald-400 mx-auto mb-4" />

          <h1 className="text-3xl sm:text-5xl font-bold text-white mb-4 tracking-tight" data-testid="marketplace-gate-headline">
            The Marketplace lives in the app.
          </h1>

          <p className="text-base sm:text-lg text-gray-400 leading-relaxed max-w-xl mx-auto mb-8" data-testid="marketplace-gate-sub">
            Buying and selling second-hand channels — followers, loops, history and all —
            happens inside the GlobalEyes.TV mobile app. Install once and you're in.
          </p>

          {/* Install rows */}
          <div className="flex flex-col sm:flex-row gap-3 justify-center mb-10" data-testid="marketplace-gate-cta-row">
            <a
              href="#"  // TODO(post-launch): swap to real App Store link
              className="flex items-center justify-center gap-3 px-5 py-3 bg-white hover:bg-gray-100 text-black rounded-xl font-medium transition-colors min-w-[200px]"
              data-testid="marketplace-gate-ios-cta"
              onClick={(e) => e.preventDefault()}
              title="Available soon on the App Store"
            >
              <Apple className="h-6 w-6" />
              <div className="text-left">
                <p className="text-[10px] uppercase tracking-wider opacity-70 leading-none">Coming soon to</p>
                <p className="text-sm font-semibold leading-tight">App Store</p>
              </div>
            </a>
            <a
              href="#"  // TODO(post-launch): swap to real Google Play link
              className="flex items-center justify-center gap-3 px-5 py-3 bg-white hover:bg-gray-100 text-black rounded-xl font-medium transition-colors min-w-[200px]"
              data-testid="marketplace-gate-android-cta"
              onClick={(e) => e.preventDefault()}
              title="Available soon on Google Play"
            >
              <Smartphone className="h-6 w-6" />
              <div className="text-left">
                <p className="text-[10px] uppercase tracking-wider opacity-70 leading-none">Coming soon to</p>
                <p className="text-sm font-semibold leading-tight">Google Play</p>
              </div>
            </a>
          </div>

          {/* Why explainer */}
          <div className="bg-gray-900/60 border border-gray-800 rounded-xl p-5 text-left mb-8" data-testid="marketplace-gate-why">
            <h2 className="text-white font-semibold text-sm flex items-center gap-2 mb-2">
              <Shield className="h-4 w-4 text-emerald-400" />
              Why is it app-only?
            </h2>
            <p className="text-gray-400 text-sm leading-relaxed">
              Channel transfers move real value — credits, follower lists, every published
              video. Keeping the transaction surface inside the signed mobile app gives us
              biometric checkout, push-notified bid alerts, and a tamper-resistant buyer
              record that the open web simply can't match.
            </p>
          </div>

          {/* Secondary CTAs to keep the visitor engaged */}
          <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-sm">
            <Link to="/" className="text-emerald-400 hover:text-emerald-300 inline-flex items-center gap-1" data-testid="marketplace-gate-back-home">
              <ArrowRight className="h-3.5 w-3.5 -rotate-180" /> Back to feed
            </Link>
            <span className="text-gray-700">·</span>
            <Link to="/dashboard" className="text-gray-400 hover:text-gray-200" data-testid="marketplace-gate-dashboard">
              Manage your channel
            </Link>
            <span className="text-gray-700">·</span>
            <Link to="/pricing" className="text-gray-400 hover:text-gray-200" data-testid="marketplace-gate-pricing">
              See plans
            </Link>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default MarketplaceAppOnlyGate;
